import { IVisibleByPlan, SOURCE } from ".";
import { ICondition, IQuestionnaire } from "./activities";
import { CUSTOM_FIELD_TYPE } from "./customFields";
import { STEP_STATUS } from "./jobs";
import { Query } from "./notifications";

export enum WORKFLOW_TYPE {
  DELIVERY_LOADING = "DELIVERY_LOADING",
  DELIVERY_SUCCESSFUL = "DELIVERY_SUCCESSFUL",
  DELIVERY_UNSUCCESSFUL = "DELIVERY_UNSUCCESSFUL",
  DELIVERY_PARTIALLY_SUCCESSFUL = "DELIVERY_PARTIALLY_SUCCESSFUL",
  DELIVERY_CHECKOUT = "DELIVERY_CHECKOUT",
  VAN_SALES_LOADING = "VAN_SALES_LOADING",
  VAN_SALES_CHECKOUT = "VAN_SALES_CHECKOUT",
}

export enum STEP_SCOPE {
  GLOBAL = "GLOBAL",
  TEAM = "TEAM",
  SINGLE_TEAM = "SINGLE_TEAM",
  MOBILE_USER = "MOBILE_USER",
  SINGLE_MOBILE_USER = "SINGLE_MOBILE_USER",
}

/**
 * The different type of steps
 */
export enum STEP_TYPE {
  ACTIVITY = "ACTIVITY",
  TIMER = "TIMER",
  NOTIFICATION = "NOTIFICATION",
  TRANSFORMATION = "TRANSFORMATION",
  STREAM = "STREAM",
  INTEGRATION = "INTEGRATION",
  CREATE_REPORT = "CREATE_REPORT",
  CREATE_PREDICTION = "CREATE_PREDICTION",
  SHARE_QUERY_RESULTS = "SHARE_QUERY_RESULTS",
  USE_PREDICTION = "USE_PREDICTION",
  START_WORKFLOW = "START_WORKFLOW",
  UPDATE_QUERY_ATTRIBUTES = "UPDATE_QUERY_ATTRIBUTES",
  MAINTENANCE = "MAINTENANCE",
  SCRIPT = "SCRIPT",
  HTTP_REQUEST = "HTTP_REQUEST",
  CREATE_ITEM = "CREATE_ITEM",
  EDIT_ITEM = "EDIT_ITEM",
  EXPORT_ACTIVITY_REPORT_PDF = "EXPORT_ACTIVITY_REPORT_PDF",
  UPLOAD_IMAGE_TO_HASTY = "UPLOAD_IMAGE_TO_HASTY",
  MIGRATION = "MIGRATION",
  WEBHOOK = "WEBHOOK",
  TRIGGER = "TRIGGER",
}

export const JOB_POSSIBLE_TO_CONFIGURE = Object.values(STEP_TYPE).filter(
  (s) =>
    ![
      STEP_TYPE.ACTIVITY, // this one is an activity, not a job really
      STEP_TYPE.MAINTENANCE, // used in maintenance default workflows
      STEP_TYPE.SHARE_QUERY_RESULTS, // used for Nestle in v2, not sure used anymore in v3
    ].includes(s)
);

export enum ASSIGNMENT_RULES {
  NONE = "NONE",
  MANUAL = "MANUAL",
  AUTO = "AUTO",
  /**
   * @deprecated use META_EXPRESSION
   */
  AUTOMATED = "AUTOMATED",
  META_EXPRESSION = "META_EXPRESSION",
}

/**
 * Step structure
 */
export interface IWorkflowStep {
  id: string;
  schema_id: string;
  type: STEP_TYPE;
  visit_number?: number;
  is_editable?: boolean;
  scope?: STEP_SCOPE;
  assignment_rules?: ASSIGNMENT_RULES;
  assignment_rules_expression?: string;
  validity_time?: number;
  allow_failure?: boolean;
  should_use_last_step_customer?: boolean;
  from_template?: boolean;
  custom_activity_report_summary?: string;
  custom_activity_report_summary_queries?: Query[];
}

export interface IStepOverview {
  id: string;
  type: STEP_TYPE;
  started_at: Date;
  ended_at?: Date;
  duration?: number;
  target?: string;
  status: STEP_STATUS;
  result?: string;
  failure_message?: string;
  system_action?: boolean;
  workflow_id?: string;
  workflow_report_id?: string;
  activity_report_id?: string;
  schema_id?: string;
  step_id?: string;
  step_name?: string;
  scope?: STEP_SCOPE;
  termination_time?: Date;
  parameters?: string;
  customer_id?: string;
  trigger_overview_id?: string;
}

export interface IStepOverviewForFrontend {
  id?: string;
  activity_report_id?: string;
  schema_id?: string;
  step_id?: string;
  step_scope?: STEP_SCOPE;
  scope_result?: string[];
  step_name?: string;
  step_status?: STEP_STATUS;
  step_type?: STEP_TYPE;
  result?: any[] | string;
  failure_message?: string;
  parameters?: string;
  started_at?: Date;
  ended_at?: Date;
  duration?: number;
  termination_time?: Date;
  customer_id?: string;
  trigger_overview_id?: string;
}

/**
 * Step builder structure
 */
export interface IStepBuilder {
  id: string;
  target: string;
  params: any;
}

/**
 * Chaining structure
 */
export interface IChaining {
  id: string;
  origin_step: string;
  destination_step: string;
  destination_params: IJobParam[];
  conditions: ICondition[];
  skip_if_conditions_failed?: boolean;
  customer_id_to_use?: string;
}

export interface IJobParam {
  key: string;
  value: any;
  type?: CUSTOM_FIELD_TYPE;
}

export interface IAbsolutePeriodValue {
  period:
    | "MINUTE"
    | "HOUR"
    | "DAY_OF_MONTH"
    | "MONTH"
    | "YEAR"
    | "DAY_OF_WEEK"
    | "WEEK";
  value: number;
}

export interface IRelativePeriodValue {
  period: "MINUTE" | "HOUR" | "DAY" | "MONTH" | "YEAR" | "WEEK";
  value: number;
}

/**
 * Workflow structure
 */
export interface IWorkflow extends IVisibleByPlan {
  id: string;
  name: string;
  unique_usage?: boolean;
  picture_url?: string;
  starting_step_id: string;
  report_title_template?: string;
  first_step_params: IJobParam[];
  steps: IWorkflowStep[];
  chainings: IChaining[];
  attached_lists?: string[];
  attached_dashboards?: string[];
  default?: boolean;
  version: number;
  active: boolean;
  created_by: string;
  created_at: Date;
  updated_by: string;
  updated_at: Date;
  index?: number;
  conditions?: ICondition[];
  latest_report?: IWorkflowReportWithStepOverviewForFrontend;
  force_keep_history?: boolean;
  allow_single_instance?: boolean;
  type?: WORKFLOW_TYPE; // used only on the mobile frontend. Not really stored in the server as such
}

export interface IWorkflowReport {
  id: string;
  name?: string;
  workflow_id: string;
  completed_at?: Date;
  completed_by?: string;
  status: STEP_STATUS;
  version: number;
  updated_by: string;
  updated_at: Date;
  updated_locally_at?: Date;
  created_locally_at?: Date;
  created_by: string;
  created_at: Date;
  created_source: SOURCE;
  updated_source: SOURCE;
  completed_source?: SOURCE;
}

/**
 * @deprecated DO NOT USE -> Instead: IWorkflowReportWithStepOverviewForFrontend
 */
export interface IWorkflowReportWithStepOverview extends IWorkflowReport {
  step_overview: {
    step_id: string;
    step_scope: STEP_SCOPE;
    step_status: STEP_STATUS;
    scope_result?: string[];
  }[];
}

export interface IWorkflowReportWithStepOverviewForFrontend
  extends IWorkflowReport {
  step_overview: IStepOverviewForFrontend[];
}

/**
 * Workflow structure
 */
export interface IWorkflow_V2 {
  id: string;
  version: number;
  name: string;
  client_id: string;
  client_name: string;
  questionnaire: IQuestionnaire;
  actif: boolean;
  picture_url?: string;
  workflow_type: string;
  workflow_task_id?: string;
  list_task_id?: string;
  scope: STEP_SCOPE;
  is_submission_editable?: boolean;
  edit_submission_offset?: number;
  unique_nplet?: Array<string>;
  nplet_list_id?: string;
  clean_nplet_items_older_than?: number;
  task_list_id?: string;
  task_generator_conditions?: ICondition[];
  submission_title_template?: string;
  place_list_id?: string;
  is_expiration_date_settable?: boolean;
  link?: any;
  upload_tasks_config?: {
    internal_submission_id: string;
    internal_user_id?: string;
    headers_to_columns?: { [s: string]: string };
  };
  mobile_user_roles?: string[]; //TODO: to remove, deprecated
  visibility?: string;
}

export interface IWorkflowReportTable {
  id: string;
  workflow_id: string;
  completed_at?: Date;
  completed_by?: string;
  status: STEP_STATUS;
  name?: string;
  version: number;
  updated_by?: string;
  updated_at?: Date;
  created_by?: string;
  created_at?: Date;
  completed_locally_at?: Date;
  updated_locally_at?: Date;
  created_source?: SOURCE;
  updated_source?: SOURCE;
  completed_source?: SOURCE;
  shared_globally?: boolean;
}

export interface IWorkflowTable {
  id: string;
  data: string;
  active: boolean;
  version: number;
  name: string;
}

export interface IOutdatedWorkflowTable {
  id: string;
  data: string;
  version: number;
  workflow_id: string;
  created_at: Date;
  created_by: string;
}
